import { Component, Inject, OnDestroy } from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoPipe } from '@ngneat/transloco';
import { Subject, takeUntil } from 'rxjs';
import { PlanService } from 'app/modules/partner/services/plan.service';
import { Plan } from 'app/modules/shared/models/plan';
import { PlanEnum } from 'app/modules/shared/enums/planEnum';
import { PlanUtil } from 'app/modules/shared/utils/plan.util';

export interface PlanOption {
    planCode: string;
    nameKey: string;
    descriptionKey: string;
    priceKey: string;
    ctaKey: string;
    features: string[];
    highlighted: boolean;
}

export interface TrialEndPlanSelectionData {
    plans: PlanOption[];
}

/**
 * Non-dismissible modal shown once in total, at the owner's first connection
 * after the end of a trial without Stripe (TrialPeriodComponent, as the server
 * decides). Presents 3 plan cards (FREE, LITE, PRO) and lets the user choose.
 */
@Component({
    selector: 'trial-end-plan-selection',
    templateUrl: './trial-end-plan-selection.component.html',
    standalone: true,
    providers: [CurrencyPipe],
    imports: [
        CommonModule,
        MatButtonModule,
        MatDialogModule,
        MatIconModule,
        TranslocoPipe,
    ],
})
export class TrialEndPlanSelectionComponent implements OnDestroy {

    plans: PlanOption[];

    /**
     * Monthly price of each plan code, formatted like the plan selection modal, from the plans
     * offered to the partner in its own currency. Empty until those plans are loaded, so that
     * no price is ever shown before it is known.
     */
    prices: { [planCode: string]: { amount: string; free: boolean } } = {};

    private _unsubscribeAll: Subject<void> = new Subject<void>();

    constructor(
        private _dialogRef: MatDialogRef<TrialEndPlanSelectionComponent>,
        @Inject(MAT_DIALOG_DATA) public data: TrialEndPlanSelectionData,
        private _planService: PlanService,
        private _currencyPipe: CurrencyPipe
    ) {
        this.plans = data?.plans || this.getDefaultPlans();

        // Prevent closing via backdrop click or escape key
        this._dialogRef.disableClose = true;

        this._planService.availablePlans$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((plans: Plan[]) => this.prices = this._buildPrices(plans || []));
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    /**
     * Select a plan and close the dialog
     */
    selectPlan(planCode: string): void {
        this._dialogRef.close(planCode);
    }

    /**
     * Monthly prices per plan code, in whole currency units as stored (never divided by 100). The
     * free plan shows 0 in the partner's currency, even when it is not among the offered plans.
     */
    private _buildPrices(plans: Plan[]): { [planCode: string]: { amount: string; free: boolean } } {
        const format = (amount: number, currencyCode: String): string =>
            this._currencyPipe.transform(amount, String(currencyCode), 'symbol', '1.0-2') ?? '';
        const prices: { [planCode: string]: { amount: string; free: boolean } } = {};

        plans.forEach(plan => {
            const free = PlanUtil.isFreePlan(plan);
            prices[String(plan.planCode)] = { amount: format(free ? 0 : plan.planPrice, plan.currencyRefCode), free };
        });

        const currencyCode = plans.find(plan => plan.currencyRefCode)?.currencyRefCode;
        if (!prices[PlanEnum.FREE] && currencyCode) {
            prices[PlanEnum.FREE] = { amount: format(0, currencyCode), free: true };
        }
        return prices;
    }

    /**
     * Default plan options if none provided
     */
    private getDefaultPlans(): PlanOption[] {
        return [
            {
                planCode: 'FREE',
                nameKey: 'features.trial_end.plans.free.name',
                descriptionKey: 'features.trial_end.plans.free.description',
                priceKey: 'features.trial_end.plans.free.price',
                ctaKey: 'features.trial_end.plans.free.cta',
                features: [
                    'features.trial_end.plans.free.features.menus',
                    'features.trial_end.plans.free.features.dishes',
                    'features.trial_end.plans.free.features.languages',
                ],
                highlighted: false
            },
            {
                planCode: 'LITE',
                nameKey: 'features.trial_end.plans.lite.name',
                descriptionKey: 'features.trial_end.plans.lite.description',
                priceKey: 'features.trial_end.plans.lite.price',
                ctaKey: 'features.trial_end.plans.lite.cta',
                features: [
                    'features.trial_end.plans.lite.features.menus',
                    'features.trial_end.plans.lite.features.dishes',
                    'features.trial_end.plans.lite.features.languages',
                    'features.trial_end.plans.lite.features.analytics',
                ],
                highlighted: false
            },
            {
                planCode: 'PRO',
                nameKey: 'features.trial_end.plans.pro.name',
                descriptionKey: 'features.trial_end.plans.pro.description',
                priceKey: 'features.trial_end.plans.pro.price',
                ctaKey: 'features.trial_end.plans.pro.cta',
                features: [
                    'features.trial_end.plans.pro.features.unlimited',
                    'features.trial_end.plans.pro.features.qr',
                    'features.trial_end.plans.pro.features.analytics',
                    'features.trial_end.plans.pro.features.ai',
                    'features.trial_end.plans.pro.features.support'
                ],
                highlighted: true
            }
        ];
    }
}
